import React, { Component, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { connect } from "react-redux";
import { Truck, Search, RefreshCw, ChevronDown, CheckCircle, ArrowLeft, ArrowRight, X, Calendar } from "react-feather";
import KidoLoader from "../common/KidoLoader";
import EmptyState from "../common/EmptyState";
import { money, sfLabel, paymentTone, Badge, PaymentSummary, ReceivePanel, ConsignmentList } from "./SteadfastParts";
import "./steadfast-kido.css";
import { getSingleOrder, applyPaymentBatch, getProcessedSteadfastPaymentIds, getSteadfastLastPage, updateSteadfastLastPage, restoreProductStockForCancelledOrder } from "../../firebase/firebase.utils";

const api_key = process.env.REACT_APP_STEADFAST_API_KEY || "";
const secret_key = process.env.REACT_APP_STEADFAST_SECRET_KEY || "";
const base_url = "https://portal.packzy.com/api/v1";

// Steadfast status → our DB orderStatus value
const SF_TO_ORDER_STATUS = {
  "delivered":         "Delivered",
  "partial_delivered": "Partial Delivered",
  "cancelled":         "Cancelled",
};

export class SteadfastPayments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      allPayments: [],
      clientPage: 1,
      singlePayment: null,
      singleLoading: false,
      activePaymentId: null,
      // Search
      searchPaymentId: "",
      // Receive payment
      receivePreview: null,
      receiveLoading: false,
      receiveConfirming: false,
      processedPaymentIds: new Set(),
    };
  }

  componentDidMount() {
    this.fetchAllPayments();
    this.loadProcessedIds();
  }

  loadProcessedIds = async () => {
    const ids = await getProcessedSteadfastPaymentIds();
    this.setState((prev) => ({
      processedPaymentIds: new Set([...prev.processedPaymentIds, ...ids]),
    }));
  };

  getMonthName = () => {
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const d = new Date();
    return `${months[d.getMonth()]},${d.getFullYear()}`;
  };

  // We only ever need the most recent ~30 payments, and Steadfast's page
  // count only grows over time — so instead of scanning a fixed page range,
  // we cache the last known last-page number in Firestore and cheaply detect
  // how far it's grown since last time, then fetch just the last few pages.
  PAGES_TO_SHOW = 3;

  fetchAllPayments = async () => {
    this.setState({ loading: true, allPayments: [], singlePayment: null, activePaymentId: null, clientPage: 1, receivePreview: null });
    try {
      const headers = { "Api-Key": api_key, "Secret-Key": secret_key, "Content-Type": "application/json" };
      const pageCache = {};
      const fetchPage = async (p) => {
        if (pageCache[p] !== undefined) return pageCache[p];
        const res = await axios.get(`${base_url}/payments?page=${p}`, { headers });
        const data = res.data.payments || [];
        pageCache[p] = data;
        return data;
      };

      const cachedLastPage = await getSteadfastLastPage();
      let lastPage;

      if (!cachedLastPage) {
        // No cache yet — exponential search from page 1 to find an upper bound
        // (a known-empty page), then binary search the exact boundary.
        let known = 0;
        let probe = 1;
        while ((await fetchPage(probe)).length > 0) {
          known = probe;
          probe *= 2;
        }
        let low = known, high = probe;
        while (low < high - 1) {
          const mid = Math.floor((low + high) / 2);
          const batch = await fetchPage(mid);
          if (batch.length > 0) low = mid; else high = mid;
        }
        lastPage = low;
      } else if ((await fetchPage(cachedLastPage)).length === 0) {
        // Cached page is now stale-high (e.g. data changed upstream) — search backward.
        let low = 0, high = cachedLastPage;
        while (low < high - 1) {
          const mid = Math.floor((low + high) / 2);
          const batch = await fetchPage(mid);
          if (batch.length > 0) low = mid; else high = mid;
        }
        lastPage = low;
      } else {
        // Cached page still has data — check whether more pages were added
        // since last time, via exponential search forward from the cache.
        let known = cachedLastPage;
        let step = 1;
        let probe = known + step;
        while ((await fetchPage(probe)).length > 0) {
          known = probe;
          step *= 2;
          probe = known + step;
        }
        if (probe > known + 1) {
          let low = known, high = probe;
          while (low < high - 1) {
            const mid = Math.floor((low + high) / 2);
            const batch = await fetchPage(mid);
            if (batch.length > 0) low = mid; else high = mid;
          }
          lastPage = low;
        } else {
          lastPage = known;
        }
      }

      updateSteadfastLastPage(lastPage);

      const firstPage = Math.max(1, lastPage - this.PAGES_TO_SHOW + 1);
      const all = [];
      for (let p = firstPage; p <= lastPage; p++) {
        const batch = await fetchPage(p);
        all.push(...batch);
      }
      this.setState({ allPayments: all.slice().reverse() });
    } catch (e) {
      toast.error("Failed to fetch payments: " + (e.response?.data?.message || e.message));
    }
    this.setState({ loading: false });
  };

  fetchPaymentDetail = async (paymentId) => {
    if (this.state.activePaymentId === paymentId && this.state.singlePayment) {
      this.setState({ singlePayment: null, activePaymentId: null, receivePreview: null });
      return;
    }
    this.setState({ singleLoading: true, singlePayment: null, activePaymentId: paymentId, receivePreview: null });
    try {
      const res = await axios.get(`${base_url}/payments/${paymentId}`, {
        headers: { "Api-Key": api_key, "Secret-Key": secret_key, "Content-Type": "application/json" },
      });
      this.setState({ singlePayment: res.data });
    } catch (e) {
      toast.error("Failed to fetch payment detail: " + (e.response?.data?.message || e.message));
      this.setState({ activePaymentId: null });
    }
    this.setState({ singleLoading: false });
  };

  handleReceivePayment = async (payment, consignments) => {
    this.setState({ receiveLoading: true, receivePreview: null });
    const preview = await Promise.all(consignments.map(async (c) => {
      try {
        const orderData = await getSingleOrder(String(c.invoice));
        if (!orderData) return { consignment: c, notFound: true };
        const order = { id: String(c.invoice), ...orderData };
        const totalBill =
          Number(order.subTotal || 0) +
          Number(order.deliveryCharge || 0) -
          Number(order.discountApplied || 0) -
          Number(order.couponApplied ? order.couponApplied.discount || 0 : 0) -
          Number(order.finalDiscount || 0);
        const alreadyPaid = (order.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
        const totalDue = totalBill - alreadyPaid;
        const alreadyApplied = (order.payments || []).some(
          (p) => String(p.transactionId) === String(c.consignment_id)
        );
        // Order already fully settled (via this or any other payment method) → skip
        const alreadyFullyPaid = totalDue <= 0;
        // Map Steadfast delivery status to our DB orderStatus
        const sfOrderStatus = SF_TO_ORDER_STATUS[c.status] || null;
        // Cancelled consignments never had COD money received — no payment/cash-in, order status only
        const isCancelled = c.status === "cancelled";
        const alreadyCancelled = isCancelled && order.orderStatus === "Cancelled";
        // Cap COD at what's actually owed — never overpay / double-apply
        const appliedAmount = isCancelled ? 0 : Math.max(0, Math.min(Number(c.cod_amount || 0), totalDue));
        const newTotalPaid = alreadyPaid + appliedAmount;
        const paymentStatus = isCancelled
          ? (order.paymentStatus || "Not Paid")
          : (newTotalPaid >= totalBill ? "Paid" : "Partially Paid");
        return {
          consignment: c,
          order,
          totalDue,
          appliedAmount,
          paymentStatus,
          sfOrderStatus,
          isCancelled,
          alreadyCancelled,
          alreadyApplied,
          alreadyFullyPaid,
        };
      } catch {
        return { consignment: c, notFound: true };
      }
    }));
    this.setState({ receivePreview: preview, receiveLoading: false });
  };

  handleConfirmReceive = async (payment) => {
    this.setState({ receiveConfirming: true });
    const { receivePreview } = this.state;
    const adminName = (this.props.currentAdmin && this.props.currentAdmin.name) || "";
    const date = new Date();
    // Skip: not found, already applied to this consignment ID, or order already fully paid
    const applicable = receivePreview.filter(
      (i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid && !i.isCancelled && i.appliedAmount > 0
    );
    // Cancelled consignments: no COD received — only sync orderStatus to "Cancelled", no payment/cash-in
    const cancelOnly = receivePreview.filter(
      (i) => !i.notFound && i.isCancelled && !i.alreadyCancelled
    );

    if (applicable.length === 0 && cancelOnly.length === 0) {
      toast.error("No applicable consignments to process.");
      this.setState({ receiveConfirming: false });
      return;
    }

    // Deterministic ids (not Date.now()-based) so re-confirming the same
    // Steadfast payment overwrites the same cash-in/expense docs instead
    // of creating duplicates.
    const ordersToUpdate = [
      ...applicable.map(({ consignment: c, order, paymentStatus, appliedAmount, sfOrderStatus }) => {
        const newPayment = {
          amount: appliedAmount,
          method: "SteadFast (COD)",
          receivedBy: adminName,
          date: date.toLocaleDateString("en-GB"),
          transactionId: String(c.consignment_id),
        };
        const payments = order.payments ? [...order.payments, newPayment] : [newPayment];
        const updatedOrder = { ...order, paymentStatus, payments };
        const patch = { paymentStatus, payments };
        // Only update orderStatus when Steadfast confirms delivery/partial delivery
        if (sfOrderStatus) {
          updatedOrder.orderStatus = sfOrderStatus;
          patch.orderStatus = sfOrderStatus;
        }
        return {
          updatedOrder,
          patch,
          dedupe: { type: "payment", transactionId: String(c.consignment_id) },
          cashIn: {
            id: `steadfast_${c.consignment_id}`,
            category: "ORDERS",
            subCategory: "SteadFast (COD)",
            month: this.getMonthName(),
            date: date.toLocaleDateString("en-GB"),
            note: `Order Id:${order.id}`,
            amount: appliedAmount,
            receiveBy: adminName,
            status: "pending",
            unEditable: true,
          },
        };
      }),
      ...cancelOnly.map(({ order, sfOrderStatus }) => ({
        updatedOrder: { ...order, orderStatus: sfOrderStatus },
        patch: { orderStatus: sfOrderStatus },
        dedupe: { type: "orderStatus", value: sfOrderStatus },
      })),
    ];

    const cashOutAmount = Number(payment.charges || 0) + Number(payment.due_bills || 0);
    const expense = {
      id: `steadfast_payment_${payment.payment_id}`,
      category: "Delivery charge",
      subCategory: String(payment.payment_id),
      month: this.getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Steadfast Payment #${payment.payment_id} | ${applicable.length} consignments | Total: ৳${payment.total} | Charges: ৳${payment.charges} | Due Bills: ${payment.due_bills}`,
      amount: cashOutAmount,
      receiveBy: adminName,
      status: "pending",
      unEditable: true,
    };

    try {
      for (const { order } of cancelOnly) {
        await restoreProductStockForCancelledOrder(order, {
          cancelNote: "Cancelled via Steadfast courier update",
          adminName,
        });
      }
      const { appliedCount, skippedCount } = await applyPaymentBatch({ ordersToUpdate, expense });
      this.setState((prev) => ({
        processedPaymentIds: new Set([...prev.processedPaymentIds, String(payment.payment_id)]),
        receivePreview: null,
        receiveConfirming: false,
      }));
      toast.success(
        `Applied for ${appliedCount} consignment(s)${skippedCount > 0 ? ` (${skippedCount} already applied, skipped)` : ""}. Cash-out: ৳${cashOutAmount}.`
      );
    } catch (e) {
      toast.error(
        "Failed partway through — some of this may already be saved. Click Receive Payment to refresh and retry what's left. " + (e.message || String(e))
      );
      this.setState({ receiveConfirming: false, receivePreview: null });
    }
  };

  // The expanded payment: slip, receive flow and consignments.
  renderDetail = (payment) => {
    const { receivePreview, receiveLoading, receiveConfirming, processedPaymentIds } = this.state;
    const consignments = payment.consignments || [];
    return (
      <div className="kd-sf-detail">
        <PaymentSummary payment={payment} />
        {consignments.length > 0 && (
          <ReceivePanel
            payment={payment}
            processed={processedPaymentIds.has(String(payment.payment_id))}
            preview={receivePreview}
            loading={receiveLoading}
            confirming={receiveConfirming}
            onReceive={() => this.handleReceivePayment(payment, consignments)}
            onConfirm={() => this.handleConfirmReceive(payment)}
            onCancel={() => this.setState({ receivePreview: null })}
          />
        )}
        <ConsignmentList consignments={consignments} />
      </div>
    );
  };

  openSearched = () => {
    const id = this.state.searchPaymentId.trim();
    if (id) window.open(`${process.env.PUBLIC_URL}/steadfast/payment-detail/${id}`, "_blank");
  };

  render() {
    const {
      loading, allPayments, clientPage, singlePayment, singleLoading, activePaymentId,
      processedPaymentIds, searchPaymentId,
    } = this.state;
    const perPage = 20;
    const totalPages = Math.ceil(allPayments.length / perPage);
    const list = allPayments.slice((clientPage - 1) * perPage, clientPage * perPage);

    const received = allPayments.filter((p) => processedPaymentIds.has(String(p.payment_id))).length;
    const totalAmount = allPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const stats = [
      { label: "Payments", value: allPayments.length.toLocaleString(), tone: "sky", Icon: Truck },
      { label: "Received", value: received.toLocaleString(), tone: "mint", Icon: CheckCircle },
      { label: "Not received", value: (allPayments.length - received).toLocaleString(), tone: "butter", Icon: Calendar },
      { label: "Paid to us", value: `৳ ${Math.round(totalAmount).toLocaleString("en-IN")}`, tone: "lilac", Icon: Truck },
    ];

    return (
      <Fragment>
        <Breadcrumb title="Steadfast Payments" parent="Courier" />
        <div className="container-fluid">
          <section className="card kd-orders-card kd-sf">
            <div className="kd-ord-top">
              <div className="kd-ord-title">
                <span className="kd-ord-title-tile kd-st--sky" aria-hidden="true">
                  <Truck size={20} />
                </span>
                <div>
                  <h2>Steadfast payments</h2>
                  <svg className="kd-squiggle" viewBox="0 0 120 10" aria-hidden="true" focusable="false">
                    <path d="M2 6 Q12 1 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" />
                  </svg>
                </div>
              </div>

              <form
                className="kd-ord-search"
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  this.openSearched();
                }}
              >
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Open a payment by ID"
                  aria-label="Payment ID"
                  value={searchPaymentId}
                  onChange={(e) => this.setState({ searchPaymentId: e.target.value })}
                />
                <button type="submit" className="kd-sf-search-go" disabled={!searchPaymentId.trim()}>
                  Open
                </button>
              </form>

              <button
                type="button"
                className="btn btn-secondary kd-sf-refresh"
                onClick={this.fetchAllPayments}
                disabled={loading}
              >
                <RefreshCw size={15} aria-hidden="true" className={loading ? "is-spinning" : ""} />
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>

            {!loading && allPayments.length > 0 && (
              <div className="kd-ord-summary">
                <div className="kd-ord-stats kd-sf-stats">
                  {stats.map(({ label, value, tone, Icon }, i) => (
                    <div key={label} className={`kd-stat-tag kd-st--${tone}`} style={{ "--i": i }}>
                      <span className="kd-stat-label">
                        <Icon size={13} aria-hidden="true" />
                        {label}
                      </span>
                      <span className="kd-stat-value">{value}</span>
                    </div>
                  ))}
                </div>
                <p className="kd-sf-scope">Latest {allPayments.length} payouts from Steadfast</p>
              </div>
            )}

            <div className="kd-sf-body">
              {/* An open payment that isn't on this page of the list */}
              {activePaymentId && singlePayment && !list.find((p) => p.payment_id === activePaymentId) && (
                <div className="kd-sf-floating">
                  <div className="kd-sf-floating-head">
                    <span className="kd-sf-pid">#{singlePayment.payment.payment_id}</span>
                    <button
                      type="button"
                      className="kd-icon-btn"
                      aria-label="Close payment"
                      onClick={() => this.setState({ singlePayment: null, activePaymentId: null, receivePreview: null })}
                    >
                      <X size={15} aria-hidden="true" />
                    </button>
                  </div>
                  {this.renderDetail(singlePayment.payment)}
                </div>
              )}

              {loading && (
                <div className="kd-sf-loading">
                  <KidoLoader inline label="Fetching payments" />
                  <p>Fetching payments — this may take a moment…</p>
                </div>
              )}

              {!loading && allPayments.length > 0 && (
                <>
                  <ul className="kd-sf-list">
                    {list.map((p, i) => {
                      const open = activePaymentId === p.payment_id && singlePayment;
                      const busy = singleLoading && activePaymentId === p.payment_id;
                      const isReceived = processedPaymentIds.has(String(p.payment_id));
                      return (
                        <li
                          key={i}
                          className={"kd-sf-row" + (open ? " is-open" : "")}
                          style={{ "--i": Math.min(i, 12) }}
                        >
                          <button
                            type="button"
                            className="kd-sf-row-main"
                            onClick={() => this.fetchPaymentDetail(p.payment_id)}
                            disabled={busy}
                            aria-expanded={!!open}
                          >
                            <span className="kd-sf-row-id">
                              <span className="kd-sf-pid">#{p.payment_id}</span>
                              <span className="kd-muted">{p.method ?? "—"}</span>
                            </span>
                            <span className="kd-sf-row-amount">
                              <span className="kd-price-sticker">{money(p.amount)}</span>
                            </span>
                            <span className="kd-sf-row-facts">
                              <span>Total <b>{money(p.total)}</b></span>
                              <span className="is-bad">Charges <b>{money(p.charges)}</b></span>
                            </span>
                            <span className="kd-sf-row-facts">
                              <span>Paid bills <b>{p.paid_bills ?? "—"}</b></span>
                              <span className={p.due_bills > 0 ? "is-bad" : ""}>Due bills <b>{p.due_bills ?? "—"}</b></span>
                            </span>
                            <span className="kd-sf-row-facts kd-sf-row-dates">
                              <span>Created {p.created_at ?? "—"}</span>
                              <span>Paid {p.paid_at ?? "—"}</span>
                            </span>
                            <span className="kd-sf-row-status">
                              <Badge tone={paymentTone(p.status_label)}>{sfLabel(p.status_label)}</Badge>
                              {isReceived ? (
                                <Badge tone="mint" dot={false}>✓ Received</Badge>
                              ) : (
                                <Badge tone="ink" dot={false}>Not Received</Badge>
                              )}
                            </span>
                            <span className="kd-sf-row-toggle" aria-hidden="true">
                              {busy ? <span className="kd-hop"><i /><i /><i /></span> : <ChevronDown size={18} />}
                            </span>
                          </button>
                          {open && this.renderDetail(singlePayment.payment)}
                        </li>
                      );
                    })}
                  </ul>

                  {totalPages > 1 && (
                    <nav className="kd-sf-pager" aria-label="Pages">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => this.setState({ clientPage: clientPage - 1 })}
                        disabled={clientPage <= 1}
                      >
                        <ArrowLeft size={14} aria-hidden="true" />
                        Newer
                      </button>
                      <span>Page {clientPage} of {totalPages}</span>
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => this.setState({ clientPage: clientPage + 1 })}
                        disabled={clientPage >= totalPages}
                      >
                        Older
                        <ArrowRight size={14} aria-hidden="true" />
                      </button>
                    </nav>
                  )}
                </>
              )}

              {!loading && allPayments.length === 0 && <EmptyState>No payments found.</EmptyState>}
            </div>
          </section>
        </div>
        <ToastContainer />
      </Fragment>
    );
  }
}

export default connect(
  (state) => ({ currentAdmin: state.admins.currentAdmin }),
)(SteadfastPayments);
